/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { type ComponentType, useCallback, memo } from 'react';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { RenderOnClient } from '@jira/ssr__ssr-toolkit/src/render-on-client.tsx';
import {
	GLOBAL_COMPONENTS_MARK,
	GLOBAL_COMPONENTS_START_MARK,
	GLOBAL_COMPONENTS_END_MARK,
} from '@jira/platform__page-container-constants/src/index.tsx';
import { useCurrentRoute } from '@jira/platform__utils-router/src/index.tsx';
import { renderGlobalComponents } from '@jira/platform__layouts-route-layout/src/GlobalComponent.tsx';
import PerformanceMark from '@atlassian/jira-common-performance/src/set-performance-mark.tsx';
import { setInitialPageLoadTimingFromPerformanceMarks } from '@atlassian/jira-spa-performance-breakdown/src/utils/performance-marks-tools/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';

export type GlobalComponentsProps = {
	sharedGlobalComponents: ReadonlyArray<ComponentType<{}>>;
};

const routeComponents: Array<never> = [];

const RouteGlobalComponents = () => {
	const route = useCurrentRoute();

	return <>{renderGlobalComponents(route.layout?.globalComponents || routeComponents)}</>;
};

// This component is necessary to ensure that the error boundary picks up errors in the renderGlobalComponents function
const SharedGlobalComponents = ({ sharedGlobalComponents }: GlobalComponentsProps) => (
	<>{renderGlobalComponents(sharedGlobalComponents)}</>
);

export const GlobalComponents: React.MemoExoticComponent<
	({ sharedGlobalComponents }: GlobalComponentsProps) => React.JSX.Element
> = memo(({ sharedGlobalComponents }: GlobalComponentsProps): React.JSX.Element => {
	const globalComponentsPerformanceTiming = useCallback(() => {
		setInitialPageLoadTimingFromPerformanceMarks(
			GLOBAL_COMPONENTS_MARK,
			GLOBAL_COMPONENTS_START_MARK,
			GLOBAL_COMPONENTS_END_MARK,
			true,
			true,
		);
	}, []);

	/**
	 * !!! DO NOT ADD INDIVIDUAL COMPONENTS TO THIS FILE !!!
	 *
	 * They should be defined as part of the `sharedGlobalComponents` constant, or defined on the route
	 * definition `layout` key.
	 *
	 * @see https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/master/jira/src/packages/platform/layouts-jira-shared-layout/README.md
	 * @see https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/master/jira/src/packages/platform/ui/layouts/README.md
	 */
	return (
		<>
			<PerformanceMark metricKey={GLOBAL_COMPONENTS_START_MARK} />
			<JSErrorBoundary
				fallback="unmount"
				id="shared-global-components"
				packageName="page-container"
			>
				<RenderOnClient
				// 💡: "sharedComponents" being rendered last cannot affect SSR, and sharedGlobalComponents are also almost empty
				// 💡: this is why it's important to RenderOnClient them all
				>
					<UFOSegment name="shared-global-components">
						<SharedGlobalComponents sharedGlobalComponents={sharedGlobalComponents} />
					</UFOSegment>
				</RenderOnClient>
			</JSErrorBoundary>
			<JSErrorBoundary fallback="unmount" id="route-global-components" packageName="page-container">
				<UFOSegment name="route-global-components">
					<RouteGlobalComponents />
				</UFOSegment>
			</JSErrorBoundary>
			<PerformanceMark
				metricKey={GLOBAL_COMPONENTS_END_MARK}
				onSet={globalComponentsPerformanceTiming}
			/>
		</>
	);
});
